import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection,
  query,
  where,
  getDocs,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

export interface UserValuationData {
  userId: string;
  email: string;
  displayName: string;
  hasViewedValuation: boolean;
  valuationData?: {
    value: number;
    method: string;
    timestamp: string;
  };
  firstAccessTime: Timestamp;
  lastAccessTime: Timestamp;
}

export const checkUserValuationAccess = async (userId: string): Promise<boolean> => {
  try {
    const userDoc = doc(db, 'users', userId);
    const userSnap = await getDoc(userDoc);
    
    if (userSnap.exists()) {
      const userData = userSnap.data() as UserValuationData;
      return !userData.hasViewedValuation;
    }
    
    // User doesn't exist, so they can access
    return true;
  } catch (error) {
    console.error('Error checking user valuation access:', error);
    // In case of error, allow access (fail-safe)
    return true;
  }
};

export const createUserRecord = async (userId: string, email: string, displayName: string): Promise<void> => {
  try {
    const userDoc = doc(db, 'users', userId);
    const userSnap = await getDoc(userDoc);
    
    if (!userSnap.exists()) {
      // Only create if user doesn't exist
      const userData: UserValuationData = {
        userId,
        email,
        displayName,
        hasViewedValuation: false,
        firstAccessTime: Timestamp.now(),
        lastAccessTime: Timestamp.now()
      };
      
      await setDoc(userDoc, userData);
    } else {
      // User exists, just update lastAccessTime and any changed info
      await updateDoc(userDoc, {
        email,
        displayName,
        lastAccessTime: Timestamp.now()
      });
    }
  } catch (error) {
    console.error('Error creating user record:', error);
  }
};

export const markValuationAsViewed = async (userId: string, valuationData: any): Promise<void> => {
  try {
    const userDoc = doc(db, 'users', userId);
    await updateDoc(userDoc, {
      hasViewedValuation: true,
      valuationData: {
        value: valuationData.value,
        method: valuationData.method,
        timestamp: valuationData.timestamp
      },
      lastAccessTime: Timestamp.now()
    });
  } catch (error) {
    console.error('Error marking valuation as viewed:', error);
  }
};

export const getUserValuationData = async (userId: string): Promise<UserValuationData | null> => {
  try {
    const userDoc = doc(db, 'users', userId);
    const userSnap = await getDoc(userDoc);
    
    if (userSnap.exists()) {
      return userSnap.data() as UserValuationData;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user valuation data:', error);
    return null;
  }
}; 